import React, { useEffect, useState } from 'react';
import { useNavigation } from '../../contexts/NavigationContext';
import { navigationItems } from '../../config/navigationConfig';

const AdminNavigation = ({ pendingCounts = {}, salonLogo, onLogout, canManageUsers = false }) => {
  const { activeTab, setActiveTab } = useNavigation();
  const [isCompactMode, setIsCompactMode] = useState(false);
  
  // Monitorar tamanho da tela para decidir se usamos modo compacto
  useEffect(() => {
    const checkScreenSize = () => {
      // Definir modo compacto para telas entre 768px e 1024px
      setIsCompactMode(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    
    // Verificar ao carregar
    checkScreenSize();
    
    // Verificar quando a tela é redimensionada
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  // Filtrar itens de navegação com base nas permissões
  const filteredNavigationItems = navigationItems.filter(item => {
    // Sempre mostrar itens base
    if (item.id !== 'users') return true;
    
    // Mostrar item de Usuários apenas se tiver permissão
    return item.id === 'users' ? canManageUsers : true;
  });
  
  // Filtrar itens visíveis no mobile
  const mobileItems = filteredNavigationItems.filter(item => item.mobileVisible);
  
  // Renderiza um item de navegação (desktop ou mobile)
  const renderNavItem = (item, isMobile = false) => {
    const badge = pendingCounts[item.id] || 0;
    const isActive = activeTab === item.id;
    
    return (
      <button 
        key={item.id}
        onClick={() => setActiveTab(item.id)}
        className={`${isMobile ? 'p-3' : 'p-3'} rounded-full relative ${
          isActive ? 'bg-blue-50' : 'hover:bg-gray-100'
        }`}
        title={item.name}
      >
        <item.icon className={`w-6 h-6 ${isActive ? 'text-blue-500' : 'text-gray-500'}`} />
        
        {/* Badge para itens com notificações */}
        {badge > 0 && (
          <span className="absolute top-0 right-0 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </button>
    );
  };
  
  // Renderiza um item de navegação desktop com texto (estilo Bookly)
  const renderDesktopNavItem = (item) => {
    const badge = pendingCounts[item.id] || 0;
    const isActive = activeTab === item.id;
    
    // No modo compacto, mostramos apenas o ícone
    if (isCompactMode) {
      return (
        <button 
          key={item.id}
          onClick={() => setActiveTab(item.id)}
          className={`p-3 rounded-full relative ${
            isActive ? 'bg-blue-50' : 'hover:bg-gray-100'
          }`}
          title={item.name}
        >
          <item.icon className={`w-6 h-6 ${isActive ? 'text-[#3b83f6]' : 'text-gray-500'}`} />
          
          {/* Badge para itens com notificações */}
          {badge > 0 && (
            <span className="absolute top-0 right-0 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
              {badge > 9 ? '9+' : badge}
            </span>
          )}
        </button>
      );
    }
    
    // Modo padrão com texto
    return (
      <button 
        key={item.id}
        onClick={() => setActiveTab(item.id)}
        className={`flex items-center w-full px-3 py-2.5 rounded-xl transition-colors ${
          isActive ? 'bg-blue-50' : 'hover:bg-gray-100'
        }`}
      >
        <item.icon className={`w-5 h-5 mr-2.5 ${isActive ? 'text-[#3b83f6]' : 'text-gray-600'}`} />
        <span className={`text-sm ${isActive ? 'text-[#3b83f6] font-medium' : 'text-gray-700'}`}>
          {item.name}
        </span>
        
        {/* Badge para itens com notificações */}
        {badge > 0 && (
          <span className="ml-auto bg-red-500 text-white text-xs w-4 h-4 flex items-center justify-center rounded-full">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Barra de navegação lateral (desktop) - Estilo Bookly */}
      <div className={`hidden md:flex flex-col border-r border-gray-200 h-screen fixed top-0 left-0 overflow-y-auto bg-white z-50 ${
        isCompactMode ? 'w-20 items-center' : 'w-56'
      }`}>
        <div className={`flex flex-col h-full ${isCompactMode ? 'p-2 items-center' : 'p-4'}`}>
          {/* Logo/Ícone do salão */}
          <div className={`flex justify-center ${isCompactMode ? 'mb-6 mt-2' : 'mb-10 mt-2'}`}>
            <img 
              src={salonLogo || "/images/logo.svg"} 
              alt="Logo" 
              className={`${isCompactMode ? 'h-8' : 'h-9'} object-contain`} 
            />
          </div>
          
          {/* Itens de navegação com texto ou apenas ícones dependendo do modo */}
          <div className={`${isCompactMode ? 'flex flex-col items-center space-y-4' : 'space-y-1.5'}`}>
            {filteredNavigationItems.map(item => renderDesktopNavItem(item))}
          </div>
          
          {/* Adicionar botão de logout se fornecido */}
          {onLogout && (
            <button 
              onClick={onLogout}
              className={isCompactMode 
                ? "mt-auto p-3 rounded-full hover:bg-gray-100"
                : "flex items-center px-3 py-2.5 rounded-xl hover:bg-gray-100 mt-auto"
              }
              title="Sair"
            >
              {isCompactMode ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2.5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="text-sm text-gray-700">Sair</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
      
      {/* Barra de navegação inferior (mobile) - Mantida como está */}
      <div className="fixed bottom-0 left-0 right-0 pb-safe bg-white border-t border-gray-200 z-10 md:hidden">
        <div className="flex justify-around items-center h-14">
          {mobileItems.map(item => renderNavItem(item, true))}
        </div>
      </div>
    </>
  );
};

export default AdminNavigation;
